/*
<summary>
   This functional component is all about rendering the Step 1 tool details fields (identification, calibration, inspection settings, ownership, loan info and remarks) for the Add/Edit Tool Management form.
   Developer: Aashish Singh, Created Date: 20-Aug-2026
</summary>
<param>No Parameter Passed</param>
<returns>Returns JSX</returns>
*/

import { Field, useFormikContext } from 'formik';
import { useEffect, useMemo } from 'react';
import { Col, Row } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import { TEXTAREA_ROW_LENGTH } from '../../../../constants/constants';
import CalibrationStatusEnum from '../../../../constants/enums/calibration-status-enum';
import InspectionCycleEnum from '../../../../constants/enums/inspection-cycle-enum';
import InspectionStatusEnum from '../../../../constants/enums/inspection-status-enum';
import LoanRecordEnum from '../../../../constants/enums/loan-record-enum';
import ToolTypeEnum from '../../../../constants/enums/tool-type-enum';
import UsageStatusEnum from '../../../../constants/enums/usage-status-enum';
import validCharacters from '../../../../constants/valid-characters';
import { IOption } from '../../../../models/ICommon';
import { IToolManagementVM } from '../../../../models/response/IToolManagementResponse';
import FormikDateFieldMUI from '../../../../shared-components/FormikFormHandlers/FormikDateFieldMUI';
import FormikFormInput from '../../../../shared-components/FormikFormHandlers/FormikFormInput';
import FormikMultiSelectWithChips from '../../../../shared-components/FormikFormHandlers/FormikMultiSelectWithChips';
import FormikFormRadioGroup from '../../../../shared-components/FormikFormHandlers/FormikFormRadioGroup';
import FormikFormSelect from '../../../../shared-components/FormikFormHandlers/FormikFormSelect';
import FormikFormTextArea from '../../../../shared-components/FormikFormHandlers/FormikFormTextArea';
import Label from '../../../../shared-components/label/label';
import preferencesStore from '../../../../core/stores/preferences-store';
import translations from '../../../../translations/translation';
import { formatMessage } from '../../../../translations/format-message';
import {
  ToolCalibrationStatusOptions,
  ToolInspectionCycleOptions,
  ToolInspectionFormatOptions,
  ToolInspectionStatusOptions,
  ToolLoanRecordOptions,
  ToolUsageStatusAddEditOptions,
} from '../../option';

interface IProps {
  factoryOptions: IOption[];
  processOptions: IOption[];
  isAddMode: boolean;
}

/**
 * This functional component renders a section title used to divide Step 1's fields into
 * visually grouped blocks (e.g. Tool Identification, Loan Information) instead of one long,
 * flat list of fields.
 * @param label: translation key shown as the section's heading
 */
const SectionTitle = ({ label }: { label: string }) => (
  <Col md={12}>
    <p className="tm-section-title">
      <FormattedMessage id={label} />
    </p>
  </Col>
);

/**
* This function is used to resolve a Process master name to display text.
*/
export const resolveProcessLabel = (name: string): string => {
  const messages = translations[preferencesStore.getLanguage]?.messages || {};
  return name in messages ? formatMessage(name) : name;
};

const StepOneToolDetails = ({
  factoryOptions,
  processOptions,
  isAddMode,
}: IProps) => {
  const { values, initialValues, setFieldValue } = useFormikContext<IToolManagementVM>();
  const usageStatusOptions = useMemo(
    () =>
      ToolUsageStatusAddEditOptions.map((option) => ({
        ...option,
        disabled: option.disabled && option.id !== initialValues.UsageStatus,
      })),
    [initialValues.UsageStatus]
  );

  const processMultiOptions = processOptions
    .filter((option) => option.id !== -1)
    .map((option) => ({ value: option.id, label: resolveProcessLabel(option.value) }));

  const isMainUnitOrApplicator =
    values.ToolType === ToolTypeEnum.MainUnit || values.ToolType === ToolTypeEnum.Applicator;

  // Calibration Status is never picked by the user - it's derived from the Calibration /
  // Usage Expiry date and the "No. of Days Until Warning" count
  useEffect(() => {
    const expiryDate = values.CalibrationExpiryDate
      ? new Date(values.CalibrationExpiryDate)
      : null;
    const warningDays = Number(values.NoOfDaysUntilWarning);

    if (!expiryDate || isNaN(expiryDate.getTime()) || isNaN(warningDays)) {
      return;
    }

    // Warning-days values above 2000 are invalid input - don't derive a status from them.
    if (warningDays > 2000) {
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expiryDate.setHours(0, 0, 0, 0);

    const warningStartDate = new Date(expiryDate);
    warningStartDate.setDate(warningStartDate.getDate() - warningDays);

    let computedStatus: CalibrationStatusEnum;
    if (today > expiryDate) {
      computedStatus = CalibrationStatusEnum.Expired;
    } else if (today > warningStartDate) {
      computedStatus = CalibrationStatusEnum.Warning;
    } else {
      computedStatus = CalibrationStatusEnum.Valid;
    }

    if (values.CalibrationStatus !== computedStatus) {
      setFieldValue('CalibrationStatus', computedStatus);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.CalibrationExpiryDate, values.NoOfDaysUntilWarning]);

  useEffect(() => {
    if (values.InspectionCycle !== InspectionCycleEnum.None) return;

    if (values.InspectionStatus !== InspectionStatusEnum.Inspected) {
      setFieldValue('InspectionStatus', InspectionStatusEnum.Inspected);
    }

    if (
      values.UsageStatus !== UsageStatusEnum.InUse &&
      values.UsageStatus !== UsageStatusEnum.OnLoan &&
      values.UsageStatus !== UsageStatusEnum.Unused
    ) {
      setFieldValue('UsageStatus', UsageStatusEnum.Unused);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.InspectionCycle]);

  return (
    <Row>
      <SectionTitle label="tool_identification_section" />
      <Col md={4}>
        <div className="form-group mb-3">
          <Label
            className="label-style col-form-label"
            required
            label="tool_management_no"
          />
          <Field
            className="form-control"
            name="ToolManagementNo"
            type="text"
            as={FormikFormInput}
            validcharacter={validCharacters}
          />
        </div>
      </Col>
      <Col md={4}>
        <div className="form-group mb-3">
          <Label
            className="label-style col-form-label"
            required
            label="tool_name_equipment_name"
          />
          <Field
            className="form-control"
            name="ToolName"
            type="text"
            as={FormikFormInput}
            validcharacter={validCharacters}
          />
        </div>
      </Col>
      <Col md={4}>
        <div className="form-group mb-3">
          <Label
            className="label-style col-form-label"
            required
            label="owner_factory"
          />
          <Field
            name="OwnerFactoryId"
            as={FormikFormSelect}
            options={factoryOptions}
            disabled={true}
            className="form-control"
          />
        </div>
      </Col>

      <Col md={4}>
        <div className="form-group mb-3">
          <Label
            className="label-style col-form-label"
            label="tool_manufacturer"
          />
          <Field
            className="form-control"
            name="ToolManufacturer"
            type="text"
            as={FormikFormInput}
            validcharacter={validCharacters}
          />
        </div>
      </Col>
      <Col md={4}>
        <div className="form-group mb-3">
          <Label
            className="label-style col-form-label"
            required
            label="tool_model"
          />
          <Field
            className="form-control"
            name="ToolModel"
            type="text"
            as={FormikFormInput}
            validcharacter={validCharacters}
          />
        </div>
      </Col>
      <Col md={4}>
        <div className="form-group mb-3">
          <Label
            className="label-style col-form-label"
            label="serial_no"
          />
          <Field
            className="form-control"
            name="SerialNo"
            type="text"
            as={FormikFormInput}
            validcharacter={validCharacters}
          />
        </div>
      </Col>

      <Col md={4}>
        <div className="form-group mb-3">
          <Label
            className="label-style col-form-label"
            label="equipment_category"
          />
          <Field
            className="form-control"
            name="EquipmentCategory"
            type="text"
            as={FormikFormInput}
            validcharacter={validCharacters}
          />
        </div>
      </Col>

      <SectionTitle label="process_inspection_status_section" />
      <Col md={4}>
        <div className="form-group mb-3">
          <Label
            className="label-style col-form-label"
            required
            label="process_used"
          />
          <Field
            name="ProcessUsed"
            as={FormikMultiSelectWithChips}
            options={processMultiOptions}
            placeholder="select_process"
          />
        </div>
      </Col>
      <Col md={4}>
        <div className="form-group mb-3">
          <Label
            className="label-style col-form-label"
            required
            label="usage_status"
          />
          <Field
            name="UsageStatus"
            as={FormikFormSelect}
            options={usageStatusOptions}
            disabled={isAddMode}
            className="form-control"
          />
        </div>
      </Col>
      <Col md={4}>
        <div className="form-group mb-3">
          <Label
            className="label-style col-form-label"
            label="inspection_status"
          />
          <Field
            name="InspectionStatus"
            as={FormikFormSelect}
            options={ToolInspectionStatusOptions}
            disabled={true}
            className="form-control"
          />
        </div>
      </Col>

      <Col md={4}>
        <div className="form-group mb-3">
          <Label
            className="label-style col-form-label"
            label="calibration_status"
          />
          <Field
            name="CalibrationStatus"
            as={FormikFormSelect}
            options={ToolCalibrationStatusOptions}
            disabled={true}
            className="form-control"
          />
        </div>
      </Col>
      <Col md={4}>
        <div className="form-group mb-3">
          <Label
            className="label-style col-form-label"
            label="no_of_days_until_warning"
          />
          <Field
            className="form-control"
            name="NoOfDaysUntilWarning"
            type="number"
            min={0}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              if (Number(event.target.value) < 0) event.preventDefault();
            }}
            as={FormikFormInput}
          />
        </div>
      </Col>
      <Col md={4}>
        <div className="form-group mb-3">
          <Label
            className="label-style col-form-label"
            required
            label="calibration_usage_expiry"
          />
          <Field
            name="CalibrationExpiryDate"
            as={FormikDateFieldMUI}
          />
        </div>
      </Col>

      <SectionTitle label="inspection_settings_section" />
      {values.InspectionCycle !== InspectionCycleEnum.None && (
        <Col md={4}>
          <div className="form-group mb-3">
            <Label
              className="label-style col-form-label"
              required
              label="inspection_format"
            />
            <Field
              name="InspectionFormat"
              as={FormikFormSelect}
              options={ToolInspectionFormatOptions}
              className="form-control"
            />
          </div>
        </Col>
      )}
      <Col md={8}>
        <div className="form-group mb-3">
          <Label
            className="label-style col-form-label"
            label="inspection_cycle"
          />
          <div className="d-flex flex-wrap gap-3 pt-2">
            <Field
              name="InspectionCycle"
              as={FormikFormRadioGroup}
              typeofvalues="number"
              options={ToolInspectionCycleOptions
                // On Applicator Swap only applies to Main Unit/Applicator tools - a General
                // Tool never gets swapped onto anything, so hide it for that tool type.
                .filter(
                  (option) =>
                    option.id !== InspectionCycleEnum.OnApplicatorSwap || isMainUnitOrApplicator
                )
                .map((option) => ({
                  label: option.value,
                  value: option.id,
                }))}
            />
          </div>
        </div>
      </Col>
      {values.InspectionCycle === InspectionCycleEnum.Periodic && (
        <Col md={4}>
          <div className="form-group mb-3">
            <Label
              className="label-style col-form-label"
              required
              label="inspection_interval"
            />
            <Field
              className="form-control"
              name="InspectionInterval"
              type="number"
              min={0}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                if (Number(event.target.value) < 0) event.preventDefault();
              }}
              as={FormikFormInput}
            />
          </div>
        </Col>
      )}

      <SectionTitle label="parts_components_section" />
      <Col md={4}>
        <div className="form-group mb-3">
          <Label
            className="label-style col-form-label"
            label="parts_manufacturer"
          />
          <Field
            className="form-control"
            name="PartsManufacturer"
            type="text"
            as={FormikFormInput}
            validcharacter={validCharacters}
          />
        </div>
      </Col>
      <Col md={4}>
        <div className="form-group mb-3">
          <Label
            className="label-style col-form-label"
            label="reel_model"
          />
          <Field
            className="form-control"
            name="ReelModel"
            type="text"
            as={FormikFormInput}
            validcharacter={validCharacters}
          />
        </div>
      </Col>
      <Col md={4}>
        <div className="form-group mb-3">
          <Label
            className="label-style col-form-label"
            label="loose_terminal_model"
          />
          <Field
            className="form-control"
            name="LooseTerminalModel"
            type="text"
            as={FormikFormInput}
            validcharacter={validCharacters}
          />
        </div>
      </Col>

      <SectionTitle label="ownership_asset_details_section" />
      <Col md={4}>
        <div className="form-group mb-3">
          <Label
            className="label-style col-form-label"
            label="managing_dept"
          />
          <Field
            className="form-control"
            name="ManagingDepartment"
            type="text"
            as={FormikFormInput}
            validcharacter={validCharacters}
          />
        </div>
      </Col>
      <Col md={4}>
        <div className="form-group mb-3">
          <Label
            className="label-style col-form-label"
            label="responsible_manager"
          />
          <Field
            className="form-control"
            name="ResponsibleManager"
            type="text"
            as={FormikFormInput}
            validcharacter={validCharacters}
          />
        </div>
      </Col>
      <Col md={4}>
        <div className="form-group mb-3">
          <Label
            className="label-style col-form-label"
            label="fixed_asset_no"
          />
          <Field
            className="form-control"
            name="FixedAssetNo"
            type="text"
            as={FormikFormInput}
            validcharacter={validCharacters}
          />
        </div>
      </Col>

      <Col md={4}>
        <div className="form-group mb-3">
          <Label
            className="label-style col-form-label"
            label="purchase_date"
          />
          <Field
            name="PurchaseDate"
            as={FormikDateFieldMUI}
          />
        </div>
      </Col>
      <Col md={4}>
        <div className="form-group mb-3">
          <Label
            className="label-style col-form-label"
            label="disposal_date"
          />
          <Field
            name="DisposalDate"
            as={FormikDateFieldMUI}
          />
        </div>
      </Col>
      <Col md={4}>
        <div className="form-group mb-3">
          <Label
            className="label-style col-form-label"
            label="location"
          />
          <Field
            className="form-control"
            name="Location"
            type="text"
            as={FormikFormInput}
            validcharacter={validCharacters}
          />
        </div>
      </Col>

      <Col md={4}>
        <div className="form-group mb-3">
          <Label
            className="label-style col-form-label"
            label="tag_id"
          />
          <Field
            className="form-control"
            name="TagId"
            type="text"
            as={FormikFormInput}
            validcharacter={validCharacters}
            disabled={values.LoanRecord === LoanRecordEnum.Yes}
          />
        </div>
      </Col>
      <Col md={4}>
        <div className="form-group mb-3">
          <Label
            className="label-style col-form-label"
            label="teachme_biz"
          />
          <Field
            className="form-control"
            name="TeachmebizUrl"
            type="text"
            as={FormikFormInput}
            validcharacter={validCharacters}
          />
        </div>
      </Col>

      {!isAddMode && (
        <>
          <SectionTitle label="loan_information_section" />
          <Col md={4}>
            <div className="form-group mb-3">
              <Label
                className="label-style col-form-label"
                label="loan_record"
              />
              <Field
                name="LoanRecord"
                as={FormikFormSelect}
                options={ToolLoanRecordOptions}
                disabled={true}
                className="form-control"
              />
            </div>
          </Col>
          <Col md={4}>
            <div className="form-group mb-3">
              <Label
                className="label-style col-form-label"
                label="loan_date"
              />
              <Field
                name="LoanDate"
                as={FormikDateFieldMUI}
                disabled={true}
              />
            </div>
          </Col>
          <Col md={4}>
            <div className="form-group mb-3">
              <Label
                className="label-style col-form-label"
                label="planned_return_date"
              />
              <Field
                name="PlannedReturnDate"
                as={FormikDateFieldMUI}
                disabled={true}
              />
            </div>
          </Col>
        </>
      )}

      <Col md={12}>
        <div className="form-group mb-3">
          <Label
            className="label-style col-form-label"
            label="remarks"
          />
          <Field
            rows={TEXTAREA_ROW_LENGTH}
            className="form-control"
            name="Remarks"
            as={FormikFormTextArea}
          />
        </div>
      </Col>
    </Row>
  );
};

export default StepOneToolDetails;
